import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { useOrganization } from '../hooks/useOrganization';
import { clientsApi } from '../api';
import { Client, CreateClientRequest } from '../types';
  
export const ClientDashboard: React.FC = () => {
  const { currentOrg } = useOrganization();
  const navigate = useNavigate();
  const toast = React.useRef<Toast>(null);

  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newClient, setNewClient] = useState<CreateClientRequest>({
    name: '',
    description: '',
  });
  const [isCreating, setIsCreating] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  // Load clients on mount and when org changes
  useEffect(() => {
    if (currentOrg) {
      loadClients();
    }
  }, [currentOrg]);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      const data = await clientsApi.listClients();
      setClients(data);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load clients',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Client name is required',
        life: 3000,
      });
      return;
    }

    try {
      setIsCreating(true);
      const created = await clientsApi.createClient(newClient);
      setClients([...clients, created]);
      setShowCreateDialog(false);
      setNewClient({ name: '', description: '' });
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Client created successfully',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to create client',
        life: 5000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const _filters = { ...filters };
    (_filters['global'] as any).value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const handleDeleteClient = (client: Client) => {
    confirmDialog({
      message: `Are you sure you want to delete "${client.name}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'ms-out ms-out-warning',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await clientsApi.deleteClient(client.id);
          setClients(clients.filter((c) => c.id !== client.id));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Client deleted successfully',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to delete client',
            life: 5000,
          });
        }
      },
    });
  };

  const handleOpenClient = (client: Client) => {
    // TODO: Navigate to client detail page
    navigate(`/clients/${client.id}`);
  };

  const actionBodyTemplate = (rowData: Client) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-folder-open"
          link
          tooltip="Open"
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => handleOpenClient(rowData)}
        />
        <Button
          icon="ms-out ms-out-delete"
          link
          tooltip="Delete"
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => handleDeleteClient(rowData)}
        />
      </div>
    );
  };

  const qbStatusBodyTemplate = (rowData: Client) => {
    if (rowData.qbo_realm_id && rowData.qbo_company_name) {
      return (
        <div className="qb-status">
          <i className="ms-out ms-out-check-circle qb-status-linked"></i>
          {rowData.qbo_company_name}
        </div>
      );
    }
    return (
      <div className="qb-status">
        <i className="ms-out ms-out-cancel qb-status-unlinked"></i>
        Not linked
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

        {!currentOrg ? (
        <Card>
          <p>Please select an organization to view clients.</p>
        </Card>
      ) : (
        <Card>
          <div className="controls-panel">
            <Button
            label="New Client"
            icon="ms-out ms-out-add"
            onClick={() => setShowCreateDialog(true)}
            disabled={!currentOrg}
            />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Search clients..."
            />
          </div>
          <DataTable
            value={clients}
            dataKey="id"
            stripedRows
            emptyMessage="No clients found. Create one to get started."
            loading={isLoading}
            size="small"
            className="clients-table"
            scrollable
            scrollHeight="calc(100vh - 280px)"
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
            paginatorTemplate={{
              layout:
                'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
              CurrentPageReport: (options) => (
                <span className="paginator-current">
                  Showing {options.first}-{options.last} of{' '}
                  <b>{options.totalRecords}</b> clients
                </span>
              ),
            }}
            filters={filters}
            globalFilterFields={['name', 'description']}
          >
            <Column field="name" header="Client Name" sortable filter className="col-name" />
            <Column field="description" header="Description" filter className="col-description" />
            <Column
              header="QuickBooks"
              body={qbStatusBodyTemplate}
              className="col-quickbooks"
            />
            <Column
              field="created_at"
              header="Created"
              body={(rowData) => new Date(rowData.created_at).toLocaleDateString()}
              sortable
              className="col-created"
            />
            <Column body={actionBodyTemplate} />
          </DataTable>
        </Card>
      )}

      <Dialog
        header="Create New Client"
        visible={showCreateDialog}
        className="create-client-dialog"
        onHide={() => {
          setShowCreateDialog(false);
          setNewClient({ name: '', description: '' });
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ms-out ms-out-close"
              onClick={() => setShowCreateDialog(false)}
              severity="secondary"
              outlined
            />
            <Button
              label="Create"
              icon="ms-out ms-out-check"
              className="p-button-success"
              onClick={handleCreateClient}
              loading={isCreating}
            />
          </div>
        }
      >
        <>
          <div className="form-field full-width">
            <label htmlFor="name">Client Name *</label>
            <InputText
              id="name"
              value={newClient.name}
              onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
              placeholder="Enter client name"
              autoFocus
            />
          </div>
          <div className="form-field full-width">
            <label htmlFor="description">Description</label>
            <InputTextarea
              id="description"
              value={newClient.description}
              onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
        </>
      </Dialog>
    </>
  );
};

export default ClientDashboard;
